import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Lock, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Reusable dialog that asks for the current user's password before running a destructive action.
 * Calls onConfirm(password) — parent should perform the delete/wipe. Errors from parent are toasted.
 */
export default function PasswordConfirmDialog({ open, onOpenChange, title, description, actionLabel = "Confirmar", danger = true, onConfirm }) {
  const [password, setPassword] = useState("");
  const [running, setRunning] = useState(false);

  useEffect(() => { if (open) { setPassword(""); setRunning(false); } }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!password) { toast.error("Escreve a tua password"); return; }
    setRunning(true);
    try {
      await onConfirm(password);
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Ação falhou");
    } finally { setRunning(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" data-testid="password-confirm-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2">
            <Lock className="w-4 h-4 text-[hsl(var(--destructive))]" /> {title}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <p className="text-sm text-muted-foreground">{description}</p>
          <div className="space-y-2">
            <Label>A tua password</Label>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoFocus
              autoComplete="current-password"
              data-testid="password-confirm-input"
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button
              type="submit"
              disabled={running || !password}
              className={danger ? "bg-[hsl(var(--destructive))] hover:bg-[#B03838]" : "bg-[hsl(var(--primary))] hover:bg-[#A21F58]"}
              data-testid="password-confirm-btn"
            >
              {running && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} {actionLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
